// @need-translate
import {isNil} from 'lodash';
import router from 'next/router';
import {AppLinkFallbackType} from '../applink/AppLinkFallbackType.enum';
import {pagewebUrlMatcher} from '../applink/PagewebUrlMatcher.singleton';
import {openCashCharge} from '../cashcharge/openCashCharge.function';
import {ApiStatusCodeToErrorMessage} from '../common/ApiStatusCodeToErrorMessage.const';
import {AGE_GRADE_TO_NUMBER_MAP} from '../common/constant';
import {getShouldSelfCertToAccessContent} from '../common/getShouldSelfCertToAccessContent';
import {t, tt} from '../common/getTranslations';
import {requestContext} from '../common/requestContext';
import {API_STATUS_MESSAGE_MAP} from '../common/shared/api/nicolas/ApiStatusMessageMap';
import {ApiStatusCodeVo as NicolasApiStatusVo} from '../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {showSimpleDialog} from '../dialog/simpleDialogHandler';
import {generateRelayRedirectUrl} from '../domain/relay/redirect/util/generateRelayRedirectUrl';
import {AgeGrade} from '../graphql/generated/types';
import {AGE_GRADE_TO_SELF_CERT_REQUEST_TYPE_MAP} from '../selfCert/constants';
import {showToast} from '../toast/toastHandler';
import {handleLoginPageweb} from '../user/handleLoginPageweb.function';
import {processSelfCertRequest} from '../user/processSelfCertRequest';
import {getSerializedPagewebError} from './getSerializedPagewebError.function';
import {handleRestrictedCountryDeniedError} from './handleRestrictionCountryDeniedError';
import {PagewebErrorType} from './PagewebErrorType.enum';
import {SerializedPagewebError} from './SerializedPagewebError.interface';

export function commonErrorHandler(error: SerializedPagewebError) {
  handler[error.type](error);
}

const handler: {
  [key in PagewebErrorType]: (error: SerializedPagewebError) => void;
} = {
  [PagewebErrorType.UnexpectedFetchError]: (error: SerializedPagewebError) => {
    showSimpleDialog({
      isMobile: requestContext.requestInfo.display.isMobile,
      title: t('error_network_title'),
      description: t('error_network_connect_check'),
      cancelButton: {
        type: 'label',
        text: t('common_close_button'),
      },
    });
  },
  [PagewebErrorType.NetworkStatusError]: (error: SerializedPagewebError) => {
    showSimpleDialog({
      isMobile: requestContext.requestInfo.display.isMobile,
      title: t('error_network_title'),
      description: t('error_network_connect_check'),
      cancelButton: {
        type: 'label',
        text: t('common_close_button'),
      },
    });
  },
  [PagewebErrorType.ApolloError]: (error: SerializedPagewebError) => {
    showSimpleDialog({
      title: error.message,
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  },
  [PagewebErrorType.AgeNotGranted]: (error: SerializedPagewebError) => {
    const isMobile = requestContext.requestInfo.display.isMobile;
    const {contentAgeGrade, userAge, userAuthenticatedAt} = error.extra || {};
    if (
      !isNil(error.extra?.['userAge']) &&
      !isNil(error.extra?.['userAuthenticatedAt']) &&
      !isNil(error.extra?.['contentAgeGrade']) &&
      getShouldSelfCertToAccessContent({
        ageGrade: contentAgeGrade as AgeGrade,
        userAge: Number(userAge),
        userAuthenticatedAt,
      })
    ) {
      processSelfCertRequest({
        selfCertRequestParams: {
          type: AGE_GRADE_TO_SELF_CERT_REQUEST_TYPE_MAP[
            contentAgeGrade as AgeGrade
          ],
          redirect_in_window: false,
        },
      });
      return;
    }

    showSimpleDialog({
      title: error?.extra?.['contentAgeGrade']
        ? t('verify_banned_toast', {
            values: {
              value:
                AGE_GRADE_TO_NUMBER_MAP[
                  error?.extra?.['contentAgeGrade'] as AgeGrade
                ],
            },
          })
        : '연령을 충족하지 못했습니다.', // error.extra['contentAgeGrade'] 는 반드시 있어야하지만 만약을 위한 fallback 메시지. steve 임의로 넣은 메시지임.(보여질리 없음)
      cancelButton: {
        type: 'label',
        text: t('common_cancel_button'),
      },
      isMobile,
    });
  },
  [PagewebErrorType.WebNotAccessibleError]: (error: SerializedPagewebError) => {
    const isMobile = requestContext.requestInfo.display.isMobile;
    showSimpleDialog({
      title: '웹에서 감상 불가',
      description: `${
        isMobile ? '모바일' : 'PC'
      } 웹에서는 볼 수 없는 회차입니다.
카카오페이지 앱으로 보실 수 있습니다.`,
      ...(isMobile && {
        okButton: {
          type: 'label',
          text: '앱으로 보기',
          onClick: () => {
            window.location.assign(
              error.extra
                ? pagewebUrlMatcher.getAppLinkByPathQuery({
                    pathQuery: `/content/${error.extra['seriesId']}/viewer/${error.extra['productId']}`,
                    schemePrefixType: 'applink',

                    fallbackType: AppLinkFallbackType.Store,
                  })
                : pagewebUrlMatcher.getCurrentAppLink({
                    schemePrefixType: 'applink',
                    fallbackType: AppLinkFallbackType.Store,
                  })
            );
          },
        },
        cancelButton: {
          type: 'label',
          text: t('common_cancel_button'),
        },
      }),
      ...(!isMobile && {
        cancelButton: {
          type: 'label',
          text: t('common_confirm_button'),
        },
      }),
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  },
  [PagewebErrorType.NicolasApiStatusError]: (error: SerializedPagewebError) => {
    handlePageBackendError(error);
  },
  [PagewebErrorType.KarinaApiStatusError]: (error: SerializedPagewebError) => {
    handlePageBackendError(error);
  },
  [PagewebErrorType.TckApiStatusError]: (error: SerializedPagewebError) => {
    handlePageBackendError(error);
  },
  [PagewebErrorType.BillingApiStatusError]: (error: SerializedPagewebError) => {
    showToast({
      message: error.message,
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  },
  [PagewebErrorType.CommentCooldownError]: (error: SerializedPagewebError) => {
    showSimpleDialog({
      isMobile: requestContext.requestInfo.display.isMobile,
      title: error.message,
      cancelButton: {
        type: 'label',
        text: t('common_confirm_button'),
      },
    });
  },
  [PagewebErrorType.UnknownError]: (error: SerializedPagewebError) => {
    showToast({
      message: error.message,
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  },
  [PagewebErrorType.CmsApiStatusError]: () => {},
  [PagewebErrorType.TalkViewerError]: () => {},
  [PagewebErrorType.GotchaError]: () => {},
  [PagewebErrorType.KakaoAuthError]: () => {},
  [PagewebErrorType.CancelledError]: () => {},
  [PagewebErrorType.ApiResponseFailError]: () => {},
  [PagewebErrorType.NeedToBuyTicketError]: () => {},
  [PagewebErrorType.ContentNotSaleError]: () => {},
};

function getCashCvr(pathname: string) {
  let result = '뷰어';
  if (pathname.includes('/buy/ticket')) {
    result = '콘텐츠홈';
  }
  if (pathname.includes('/more') || pathname === '/') {
    result = '더보기';
  }
  if (pathname.includes('/history/cash')) {
    result = '캐시내역';
  }
  if (pathname.includes('/set-overview')) {
    result = '세트상품';
  }
  return result;
}

function handlePageBackendError(error: SerializedPagewebError) {
  if (error.resultCode === NicolasApiStatusVo.InactiveAccount) {
    window.location.assign('/inactiveaccount');
  } else if (error.resultCode === NicolasApiStatusVo.CashNotEnough) {
    const seriesId = router?.query?.seriesId;
    const productId = router?.query?.productId;
    openCashCharge({
      returnUrl: generateRelayRedirectUrl(window.location.href),
      cashChargeInfo: {
        event_series_id: seriesId ? String(seriesId) : undefined,
        event_episode_id: productId ? String(productId) : undefined,
        cash_cvr: getCashCvr(window.location.pathname),
      },
    });
  } else if (error.resultCode === NicolasApiStatusVo.VodDeviceLimitOver) {
    showSimpleDialog({
      isMobile: requestContext.requestInfo.display.isMobile,
      title: '등록 가능한 기기수 초과',
      description:
        '회차를 보거나 다운로드 하려면\n기기등록관리에서 다른 기기 해제 후\n등록해주세요.',
      okButton: {
        type: 'label',
        text: '기기등록 관리',
        onClick: () => {
          window.location.assign('/device-management');
        },
      },
      cancelButton: {
        type: 'label',
        text: t('common_cancel_button'),
      },
    });
  } else if (error.resultCode === NicolasApiStatusVo.NotAuthorized) {
    showSimpleDialog({
      title: t('common_login'),
      description: t('alert_message_login_required'),
      isMobile: requestContext.requestInfo.display.isMobile,
      // TODO: sign-off 가 얼마 안남아 일단 웹뷰에선 로그인 버튼만 제외하고 다음 스프린트에 sessionExpired 로 통일해야함
      ...(!requestContext.requestInfo.display.isWebView
        ? {
            okButton: {
              text: t('common_login'),
              type: 'label',
              onClick: () => {
                handleLoginPageweb();
              },
            },
          }
        : {}),
      cancelButton: {text: t('common_close_button'), type: 'label'},
    });
  } else if (error.resultCode === NicolasApiStatusVo.KakaoError) {
    showSimpleDialog({
      title: tt(ApiStatusCodeToErrorMessage[NicolasApiStatusVo.KakaoError]),
      isMobile: requestContext.requestInfo.display.isMobile,
      cancelButton: {text: t('common_close_button'), type: 'label'},
    });
  } else if (error.resultCode === NicolasApiStatusVo.ItemNotSales) {
    showSimpleDialog({
      title: error.message,
      isMobile: requestContext.requestInfo.display.isMobile,
      cancelButton: {
        type: 'label',
        text: t('common_confirm_button'),
      },
    });
  } else if (error.resultCode === NicolasApiStatusVo.TodayGiftAlreadyReceived) {
    showToast({
      message: tt(API_STATUS_MESSAGE_MAP[error.resultCode]),
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  } else if (
    error.resultCode === NicolasApiStatusVo.ItemAccessRestrictionCountryDenied
  ) {
    handleRestrictedCountryDeniedError(getSerializedPagewebError(error));
  } else if (error.resultCode === NicolasApiStatusVo.ServerMaintenance) {
    // 서버 점검중에는 전체 API 가 점검중이 되기 때문에 새로고침 하면 전체 점검 화면이 뜨게 됩니다
    window.location.reload();
  } else if (error.resultCode === NicolasApiStatusVo.SetNotOnSale) {
    showSimpleDialog({
      title: t('api_content_set_not_sales'),
      isMobile: requestContext.requestInfo.display.isMobile,
      cancelButton: {
        type: 'label',
        text: t('common_confirm_button'),
      },
    });
  } else if (error.resultCode === NicolasApiStatusVo.TicketInfoInvalid) {
    showToast({
      message: tt(API_STATUS_MESSAGE_MAP[error.resultCode]),
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  } else if (
    error.resultCode === NicolasApiStatusVo.ErrorShowServerMsg ||
    error.resultCode === NicolasApiStatusVo.TodayGiftNotTargetUser
  ) {
    showSimpleDialog({
      title: error.message,
      isMobile: requestContext.requestInfo.display.isMobile,
      cancelButton: {
        type: 'label',
        text: t('common_confirm_button'),
      },
    });
  } else {
    showSimpleDialog({
      title:
        (error.resultCode && tt(API_STATUS_MESSAGE_MAP[error.resultCode])) ||
        t('error_api_toast'),
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  }
}
